import { useEffect } from "react";
import { ITAAS_ORGANIZATION_ID } from "@itaas/pep-contracts";
import { SITE_URL } from "../../config";

/** Default social preview (1200×630) — branded, not a Home screenshot. */
export const DEFAULT_OG_IMAGE_PATH = "/assets/og/og-default.jpg";

export interface SeoProps {
  title: string;
  description: string;
  keywords?: string[];
  canonicalPath?: string;
  /** Absolute URL or site-relative path for og:image / twitter:image */
  imageUrl?: string;
  jsonLd?: Record<string, unknown>;
  locale?: "fr" | "en";
  /** When true, emit robots noindex,nofollow (private / transactional pages). */
  noIndex?: boolean;
  /** Open Graph type — default website; use product for catalog detail when honest. */
  ogType?: "website" | "product";
}

function formatDocumentTitle(title: string): string {
  const trimmed = title.trim();
  if (!trimmed) return "ITaaS Suite";
  // Already branded (Home / product family names) — avoid “Title | ITaaS | ITaaS”.
  if (/^ITaaS\b/i.test(trimmed) || /\|\s*ITaaS(\s+Suite)?\s*$/i.test(trimmed)) {
    return trimmed;
  }
  return `${trimmed} | ITaaS`;
}

function absoluteAssetUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${SITE_URL}${path}`;
}

export function SeoHead({
  title,
  description,
  keywords,
  canonicalPath,
  imageUrl = DEFAULT_OG_IMAGE_PATH,
  jsonLd,
  locale = "fr",
  noIndex = false,
  ogType = "website",
}: SeoProps) {
  useEffect(() => {
    document.title = formatDocumentTitle(title);

    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const ogImage = absoluteAssetUrl(imageUrl);

    setMeta("description", description);
    if (keywords?.length) setMeta("keywords", keywords.join(", "));
    setMeta("robots", noIndex ? "noindex, nofollow" : "index, follow");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:locale", locale === "fr" ? "fr_FR" : "en_US", "property");
    setMeta("og:site_name", "ITaaS Suite", "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:image:width", "1200", "property");
    setMeta("og:image:height", "630", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);

    const canonical = canonicalPath ? `${SITE_URL}${canonicalPath === "/" ? "" : canonicalPath}` : undefined;
    if (canonical) {
      setMeta("og:url", canonical, "property");
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = canonical;

      // hreflang only when distinct locale URLs exist. Same-URL fr/en pairs are INVALID for Google.
      // Until PEP locale routing ships, rely on html[lang] only (see PepI18nProvider).
      for (const alt of ["fr", "en"] as const) {
        document.getElementById(`pep-hreflang-${alt}`)?.remove();
      }
      document.getElementById("pep-hreflang-x-default")?.remove();
    }

    let script: HTMLScriptElement | null = null;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "pep-page-jsonld";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      script?.remove();
    };
  }, [title, description, keywords, canonicalPath, imageUrl, jsonLd, locale, noIndex, ogType]);

  return null;
}

/**
 * Honest SoftwareApplication JSON-LD — no invented price, rating, review, or stock claims.
 * Includes BreadcrumbList for product detail discoverability.
 */
export function productJsonLd(product: {
  displayName: string;
  description: string;
  slug: string;
  status: string;
  keywords?: string[];
}) {
  const url = `${SITE_URL}/products/${product.slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        "@id": `${url}#software`,
        name: product.displayName,
        description: product.description,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url,
        publisher: { "@id": ITAAS_ORGANIZATION_ID },
        creativeWorkStatus: product.status,
        ...(product.keywords?.length ? { keywords: product.keywords.join(", ") } : {}),
        isPartOf: { "@id": `${SITE_URL}/#website` },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Produits",
            item: `${SITE_URL}/products`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: product.displayName,
            item: url,
          },
        ],
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: product.displayName,
        description: product.description,
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: { "@id": `${url}#software` },
      },
    ],
  };
}

/** FAQPage JSON-LD — only real Q/A pairs; no invented answers. */
export function faqPageJsonLd(args: {
  canonicalPath: string;
  name: string;
  description: string;
  questions: Array<{ question: string; answer: string }>;
}) {
  const url = `${SITE_URL}${args.canonicalPath}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: args.name,
        description: args.description,
        isPartOf: { "@id": `${SITE_URL}/#website` },
      },
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        url,
        mainEntity: args.questions.map((q) => ({
          "@type": "Question",
          name: q.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: q.answer,
          },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "ITaaS Suite",
            item: SITE_URL,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: args.name,
            item: url,
          },
        ],
      },
    ],
  };
}

/** Generic WebPage (+ optional Service) for commercial hub pages. */
export function hubPageJsonLd(args: {
  canonicalPath: string;
  name: string;
  description: string;
  serviceName?: string;
  serviceType?: string;
}) {
  const url = `${SITE_URL}${args.canonicalPath}`;
  const graph: Record<string, unknown>[] = [
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: args.name,
      description: args.description,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      publisher: { "@id": ITAAS_ORGANIZATION_ID },
    },
  ];
  if (args.serviceName) {
    graph.push({
      "@type": "Service",
      "@id": `${url}#service`,
      name: args.serviceName,
      description: args.description,
      serviceType: args.serviceType ?? "BusinessSoftware",
      provider: { "@id": ITAAS_ORGANIZATION_ID },
      url,
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}
